/* eslint-disable react/no-this-in-sfc */
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import type { Options } from "highcharts";
import colors from "tailwindcss/colors";

export type SparklineSeriesNode = {
  x?: Date | number;
  y: number;
};

interface SparklineChartProps {
  type?: "line" | "area" | "column";
  accessibilityDescription?: string;
  primaryColor?: string;
  secondaryColor?: string;
  data: SparklineSeriesNode[];
  className?: string;
}

export default function SparklineChart({
  type = "area",
  accessibilityDescription,
  primaryColor = colors.purple[50],
  secondaryColor = colors.purple[600],
  data,
  className,
}: SparklineChartProps) {
  const options: Options = {
    title: {
      text: "",
    },
    subtitle: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    accessibility: {
      enabled: !!accessibilityDescription,
      description: accessibilityDescription,
    },
    scrollbar: {
      enabled: false,
    },
    chart: {
      borderWidth: 0,
      margin: 0,
      backgroundColor: "transparent",
    },
    xAxis: {
      startOnTick: false,
      endOnTick: false,
      tickPositions: [],
      lineWidth: 0,
      gridLineWidth: 0,
      labels: {
        enabled: false,
      },
      title: {
        text: null,
      },
      grid: {
        enabled: false,
      },
    },
    yAxis: {
      endOnTick: false,
      startOnTick: false,
      tickPositions: [0],
      lineWidth: 0,
      gridLineWidth: 0,
      labels: {
        enabled: false,
      },
      title: {
        text: null,
      },
      grid: {
        enabled: false,
      },
    },
    legend: {
      enabled: false,
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
    series: [
      {
        type,
        data: data.map(({ x, y }, index) => {
          return [x === undefined ? index : x, y];
        }),
      },
    ],
    plotOptions: {
      series: {
        enableMouseTracking: false,
        animation: false,
        shadow: false,
        states: {
          hover: {
            lineWidth: 2,
          },
        },
        marker: {
          radius: 1,
          states: {
            hover: {
              radius: 2,
            },
          },
        },
      },
      line: {
        color: secondaryColor,
        lineWidth: 2,
        marker: {
          enabled: false,
        },
      },
      area: {
        color: primaryColor,
        lineColor: secondaryColor,
        lineWidth: 2,
        marker: {
          enabled: false,
        },
      },
      column: {
        color: primaryColor,
        borderColor: secondaryColor,
        borderWidth: 1,
        borderRadius: {
          where: "end",
          radius: 2,
          scope: "stack",
        },
      },
    },
  };

  return <HighchartsChart className={className} options={options} />;
}
